/**
 * Delivery footer — neighborhoods grid, FAQ, final CTA.
 */
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Phone } from "lucide-react";
import { FAQ } from "./deliveryData";

export default function DeliveryFooter({ cities }) {
    return (
        <>
            {cities.length > 0 && (
                <section
                    data-testid="delivery-neighborhoods"
                    className="bg-zen-surface border-b border-zen-line"
                >
                    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12">
                        <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-4">
                            · 06 ·
                        </p>
                        <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-zen-ink leading-tight tracking-tight mb-6 max-w-3xl">
                            Browse delivery by neighborhood.
                        </h2>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-2">
                            {cities.slice(0, 60).map((c) => (
                                <Link
                                    key={c.slug}
                                    to={`/dispensary-near/${c.slug}`}
                                    data-testid={`delivery-neighborhood-${c.slug}`}
                                    className="text-sm text-zen-ink-soft hover:text-zen-olive py-1 inline-flex items-center gap-1.5 group"
                                >
                                    <ArrowUpRight
                                        size={11}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity text-zen-olive"
                                    />
                                    Delivery to {c.city}
                                </Link>
                            ))}
                        </div>
                        <Link
                            to="/locations"
                            className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-zen-olive border-b border-zen-olive/40 pb-0.5"
                        >
                            All locations <ArrowRight size={12} />
                        </Link>
                    </div>
                </section>
            )}

            {/* FAQ */}
            <section
                data-testid="delivery-faq"
                className="bg-zen-bg border-b border-zen-line"
            >
                <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-12">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-4">
                        · 07 ·
                    </p>
                    <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-zen-ink leading-tight tracking-tight mb-6 max-w-3xl">
                        Delivery FAQ.
                    </h2>
                    <div className="space-y-1">
                        {FAQ.map((item, i) => (
                            <details
                                key={i}
                                data-testid={`delivery-faq-${i}`}
                                className="group border-b border-zen-line bg-zen-surface [&_summary::-webkit-details-marker]:hidden"
                            >
                                <summary className="cursor-pointer px-5 py-5 flex items-center justify-between gap-4 text-left">
                                    <span className="font-serif text-lg text-zen-ink font-light leading-snug">
                                        {item.q}
                                    </span>
                                    <span className="text-zen-olive shrink-0 transition-transform group-open:rotate-45">
                                        +
                                    </span>
                                </summary>
                                <div className="px-5 pb-6 text-sm text-zen-ink-soft leading-relaxed max-w-3xl">
                                    {item.a}
                                </div>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="bg-zen-elevated text-zen-cream">
                <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-10 sm:py-14 text-center">
                    <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight mb-8">
                        Ready when you are.
                    </h2>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <Link
                            to="/menu/queens-long-island"
                            className="bg-zen-cream text-zen-ink px-7 py-3.5 text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-zen-bg transition-colors inline-flex items-center gap-2"
                        >
                            Order from Queens & LI <ArrowRight size={13} />
                        </Link>
                        <Link
                            to="/menu/staten-island"
                            className="border border-zen-cream/60 text-zen-cream px-7 py-3.5 text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-zen-cream hover:text-zen-ink transition-colors inline-flex items-center gap-2"
                        >
                            Order from Staten Island <ArrowRight size={13} />
                        </Link>
                        <a
                            href="tel:17188890240"
                            className="border border-zen-cream/60 text-zen-cream px-7 py-3.5 text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-zen-cream hover:text-zen-ink transition-colors inline-flex items-center gap-2"
                        >
                            <Phone size={13} /> (718) 889-0240
                        </a>
                    </div>
                    <p className="text-xs opacity-60 mt-8">
                        For use only by adults 21+ · NY-licensed adult-use dispensary ·
                        Keep cannabis out of reach of children and pets.
                    </p>
                </div>
            </section>
        </>
    );
}
